import './RadioGroup.css';

import { useEffect, useRef } from 'react';
import { useRadioLogic } from '@sber-site/sbercraft-nova-kit/hooks/useRadioLogic';
import block from '@sber-site/sbercraft-nova-kit/utils/bem-cn-lite';
import useFocusLogic from '@sber-site/sbercraft-nova-kit/components/Tabs/useFocusLogic';
import type { IRadioGroup } from '@sber-site/sbercraft-nova-kit/components/RadioGroup/IRadioGroup';

const cn = block('radio');

function RadioGroup<T>(props: IRadioGroup<T>): React.JSX.Element {
    const {
        size = 'md',
        checked,
        type,
        options,
        disabled,
        ariaLabelledby,
        className,
        onChange,
        handleChangeOption,
    } = props;

    const checkedIndex = options?.findIndex(option => String(option.value) === String(checked));

    useEffect(() => {
        handleChangeOption(checkedIndex);
    }, [checkedIndex]);

    const radio = useRef(null);

    const pureSuffix = type === 'pure' ? `-${type}` : '';
    const focusLogic = useFocusLogic(radio, cn(`control${pureSuffix}`), checked);
    const { handleClick, handleKeyDown } = useRadioLogic({
        focusLogic,
        options,
        checked,
        disabled,
        onChange,
    });

    return (
        <div
            className={cn(
                {
                    horizontal: type === 'horizontal',
                    vertical: type === 'vertical',
                    adaptive: type === 'adaptive',
                    size,
                },
                className
            )}
            role='radiogroup'
            ref={radio}
            aria-labelledby={ariaLabelledby}
        >
            {options.map((item, i) => {
                if (!!item?.value && !!item?.successHeader) {
                    return (
                        <div className={cn(`wrap${pureSuffix}`, { size })} key={item.value as unknown as string}>
                            <div
                                className={cn(
                                    `control${pureSuffix}`,
                                    { size, checked: item.value === checked, disabled },
                                    'retention__survery-radio',
                                    item.className || ''
                                )}
                                data-value={item.value}
                                aria-checked={item.value === checked}
                                tabIndex={
                                    item.value === checked || (typeof checked === 'undefined' && i === 0) ? 0 : -1
                                }
                                onClick={() => {
                                    handleClick(item.value);
                                }}
                                onKeyDown={event => {
                                    handleKeyDown(item.value, event);
                                }}
                                role='radio'
                            >
                                {item.value}
                            </div>
                        </div>
                    );
                }
            })}
        </div>
    );
}

export default RadioGroup;
